import type { PlanVo } from "@/api";
import { firstPurchasePromoStore } from "@/store/firstPurchasePromo";
import { pricingStore } from "@/store/pricing";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { twMerge } from "tailwind-merge";
import { isFreePlan } from "../Pricing.helpers";

type BillingCycle = "MONTHLY" | "YEARLY";

interface BillingCycleToggleProps {
  value: BillingCycle;
  onChange: (cycle: BillingCycle) => void;
}

const OPTIONS: Array<{ label: string; value: BillingCycle }> = [
  { label: "Yearly", value: "YEARLY" },
  { label: "Monthly", value: "MONTHLY" },
];

// Tailwind extraction for layout consistency
const TOGGLE_NAV_WIDTH = "w-[160px] md:w-[187px]";
const LABEL_LAYOUT = `relative flex ${TOGGLE_NAV_WIDTH} h-10 cursor-pointer items-center justify-center gap-1 rounded-full`;

export const BillingCycleToggle = observer(function BillingCycleToggle({
  value,
  onChange,
}: BillingCycleToggleProps) {
  const couponDiscount = firstPurchasePromoStore.shouldShowPromo
    ? firstPurchasePromoStore.couponData?.discount?.value || 0
    : 0;

  const planList = pricingStore.planPage.planList || [];
  const paidPlans = planList.filter((p) => !isFreePlan(p));
  // Pick the recommended plan or the first paid plan as representative
  const repPlan = paidPlans.find((p) => p.recommend) || paidPlans[0];

  const getDiscountLabel = (type: BillingCycle): string => {
    if (type === "MONTHLY") {
      return couponDiscount ? `-${couponDiscount}%` : "";
    }

    if (!repPlan) return "";

    // Find Yearly and Monthly versions of the same plan
    const yPlan = planList.find(
      (p: PlanVo) =>
        p.memberCode === repPlan.memberCode && p.intervalType === "YEARLY",
    );
    const mPlan = planList.find(
      (p: PlanVo) =>
        p.memberCode === repPlan.memberCode && p.intervalType === "MONTHLY",
    );

    if (!yPlan || !mPlan) return "";

    const yPriceMonthly =
      (Number(yPlan.price || 0) / 12) * (1 - couponDiscount / 100);
    const mPrice = Number(mPlan.price || 0);

    if (mPrice === 0) return "";

    const savings = Math.ceil((1 - yPriceMonthly / mPrice) * 100);
    return savings > 0 ? `-${savings}%` : "";
  };

  return (
    <fieldset aria-label="Billing cycle" className="mt-7 text-center">
      <legend className="sr-only">Billing cycle</legend>
      <div className="relative inline-flex items-center select-none rounded-full text-sm font-medium bg-[#16151C] border border-[rgba(255,255,255,0.07)]">
        {/* Animated Background Slider */}
        <div
          className={twMerge(
            "absolute top-[4px] left-[4px] h-[32px] bg-[#272141] backdrop-blur-sm rounded-full transition-transform duration-300 ease-in-out",
            TOGGLE_NAV_WIDTH,
          )}
          style={{
            transform: `translateX(${value === "YEARLY" ? "0" : "calc(100% - 8px)"})`,
          }}
        />

        {OPTIONS.map((option) => {
          const isActive = value === option.value;
          const displayDiscount = getDiscountLabel(option.value);

          return (
            <label key={option.value} className={LABEL_LAYOUT}>
              <input
                type="radio"
                name="billing"
                value={option.value}
                className="sr-only"
                checked={isActive}
                onChange={() => onChange(option.value)}
              />
              <span
                className={classNames("transition-colors duration-150", {
                  "text-white font-extrabold": isActive,
                  "text-white/60": !isActive,
                })}
              >
                {option.label}
              </span>
              {displayDiscount && (
                <span className="sm:absolute right-3 ml-1 px-[10px] h-5 flex items-center justify-center rounded-full text-white bg-[#FF6752] text-xs font-bold">
                  {displayDiscount}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
});
